import PptxGenJS from "pptxgenjs";
import { map, times, toNumber } from "lodash";
import { COLORS, TEXT_OPTIONS } from "../masters/components";
import { TemplateData } from "../index";
import {
  getBucketFromScore,
  LSpanKey,
} from "@/lib/leadership-span-bucket-score-parser";

const LEFT_COLUMN_X = 0.52;
const COLUMN_Y = 2.07;

export const addPsychLeadershipSpanSlide = (
  pres: PptxGenJS,
  data: TemplateData
) => {
  const slide = pres.addSlide({ masterName: "SPLIT_BOTTOM_SMALL_MASTER" });
  slide.addText("Leadership Span", { placeholder: "title" });

  if (data.candidateName) {
    slide.addText(data.candidateName, {
      ...TEXT_OPTIONS.caption,
      x: LEFT_COLUMN_X,
      y: 1.14,
      fontSize: 18,
    });
  }

  slide.addText(
    [
      {
        text: "Interpretation",
        options: {
          ...TEXT_OPTIONS.caption,
          align: "left",
        },
      },
      {
        text: "Results are informed by the executive’s performance on the online assessments and any additional information that was gathered. These scores represent an executive’s fit on each of the eight Leadership Span™ differentiators and fall into Low, Moderate, and High categories. For a more complete understanding of this individual's Leadership Span™ capabilities, RRA recommends building on these results with a competency-based interview.",
        options: {
          ...TEXT_OPTIONS.body,
          align: "left",
        },
      },
    ],
    {
      x: 0.51,
      y: 6.8,
      w: 7.36,
      h: 1.46,
    }
  );

  slide.addText(
    [
      {
        text: "Background",
        options: {
          ...TEXT_OPTIONS.caption,
          align: "left",
        },
      },
      {
        text: "Through a series of studies on effective leadership, Russell Reynolds Associates (RRA) determined that the best executives are defined less by a set of single competencies and more by their ability to span critical competencies in the face of changing circumstances. Built in partnership with Hogan Assessment Systems, our model captures an executive's tendencies and approach toward balancing the natural tensions leaders navigate every day.",
        options: {
          ...TEXT_OPTIONS.body,
          align: "left",
        },
      },
    ],
    {
      x: 8.4,
      y: 6.8,
      w: 7.36,
      h: 1.46,
    }
  );

  const getSpanScoreCells = (
    score: number,
    reverse: boolean = false,
    span: number = 4
  ) => {
    const cells = times(span, (i) => ({
      text: "",
      options: {
        colspan: 1,
        fill: score >= i + 1 ? COLORS.BLUE_LIGHT : COLORS.WHITE,
        border: score >= i + 1 ? { color: COLORS.WHITE, pt: 1 } : undefined,
      },
    }));

    if (reverse) {
      return cells.reverse();
    }

    return cells;
  };

  const leadershipSpan = data.leadershipSpan || [];

  const leadershipSpanKeyDuples: [LSpanKey, LSpanKey][] = [
    ["disruptive", "pragmatic"],
    ["risk_taking", "measured"],
    ["heroic", "vulnerable"],
    ["galvanizing", "connecting"],
  ];

  const differentiatorsTableData = leadershipSpanKeyDuples.map(
    ([leftKey, rightKey]) => {
      const leftDimension = leadershipSpan.find(
        (dimension) => dimension.code === leftKey
      );
      const rightDimension = leadershipSpan.find(
        (dimension) => dimension.code === rightKey
      );
      return {
        left: {
          title: leftDimension?.title,
          description: leftDimension?.description,
          span: getBucketFromScore(
            toNumber(leftDimension?.score.value),
            leftKey
          ),
        },
        right: {
          title: rightDimension?.title,
          description: rightDimension?.description,
          span: getBucketFromScore(
            toNumber(rightDimension?.score.value),
            rightKey
          ),
        },
      };
    }
  );

  const differentiatorsTable = [
    [
      {
        text: "First Level",
        options: {
          ...TEXT_OPTIONS.caption,
          align: "left",
          valign: "middle",
          colspan: 3,
          fill: COLORS.BLUE_TABLE,
        },
      },
      {
        text: "◄  STRENGTH",
        options: {
          ...TEXT_OPTIONS.columnTitle,
          align: "left",
          valign: "middle",
          colspan: 3,
        },
      },
      { text: "", options: { colspan: 3, ...TEXT_OPTIONS.columnTitle } },
      {
        text: "STRENGTH  ►",
        options: {
          ...TEXT_OPTIONS.columnTitle,
          align: "right",
          valign: "middle",
          colspan: 3,
        },
      },
      { text: "", options: { colspan: 3, ...TEXT_OPTIONS.columnTitle } },
    ],
    ...map(differentiatorsTableData, (row) => [
      {
        text: row.left.description,
        options: {
          ...TEXT_OPTIONS.body,
          align: "left",
          valign: "middle",
          fontSize: 9,
          colspan: 3,
          w: 3,
          fill: COLORS.BLUE_LIGHT_TABLE,
        },
      },
      ...getSpanScoreCells(row.left.span, true, 3),
      {
        text: `${row.left.title}`,
        options: {
          ...TEXT_OPTIONS.body,
          align: "right",
          valign: "middle",
          fontSize: 10,
          w: 1.24,
          fill: COLORS.BLUE_LIGHT_TABLE,
        },
      },
      {
        text: "◄►",
        options: {
          ...TEXT_OPTIONS.body,
          align: "center",
          valign: "middle",
          fontSize: 10,
          color: COLORS.GRAY,
          w: 0.5,
          fill: COLORS.BLUE_LIGHT_TABLE,
        },
      },
      {
        text: `${row.right.title}`,
        options: {
          ...TEXT_OPTIONS.body,
          align: "left",
          valign: "middle",
          fontSize: 10,
          w: 1.24,
          fill: COLORS.BLUE_LIGHT_TABLE,
        },
      },
      ...getSpanScoreCells(row.right.span, false, 3),
      {
        text: row.right.description,
        options: {
          ...TEXT_OPTIONS.body,
          align: "left",
          valign: "middle",
          fontSize: 9,
          colspan: 3,
          w: 3,
          fill: COLORS.BLUE_LIGHT_TABLE,
        },
      },
    ]),
  ];

  slide.addTable(
    differentiatorsTable as any,
    {
      x: 0.46,
      y: 1.84,
      h: 4.28,
      w: 14.96,
      colW: 1,
      rowH: 0.89,
      colspan: 16,
      fill: COLORS.WHITE,
    } as any
  );
};
